import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/notifications/styles.css";
import "./styles/global.scss";

import * as Sentry from "@sentry/react";

import { BaselinePage, FollowUpPage, OverviewPage, SummaryPage } from "@components/test_entry/gpa";
import { BlogDetail, BlogEditor, BlogList } from "@pages/blog";
import {
  CognitiveProfileOverview,
  ConcussionOverview,
  LazyEyeRiskOverview,
  NeurologicalAssessment,
  ResultsSummary,
  VisionTestResults,
} from "@components/overview_pages";
import {
  DefaultConfigs,
  DistributorManagement,
  ProductManagement,
  ProductStatusPage,
  ScopeManagement,
  SystemLogs,
} from "@components/management";
import { NewReleasePage, ReleasePage, VersionManagementPage } from "@components/headset_releases";
import {
  NEW_RELEASE_PATH,
  VERSION_MANAGEMENT_PATH,
  releasePagePath,
} from "@components/headset_releases/releaseRoutes";
import { LoadingOverlay, MantineProvider } from "@mantine/core";
import { MantineEmotionProvider, emotionTransform } from "@mantine/emotion";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Suspense, useEffect, useState } from "react";
import mainTheme, { cssVariablesResolver } from "./theme";

import AdminDashboard from "@components/admin_dashboard/AdminDashboard";
import AuditTrail from "@components/audit_trail/AuditTrail";
import AdminTutorialPage from "@components/tutorial/AdminTutorialPage";
import AnalyticsDashboard from "@components/analytics_dashboard/AnalyticsDashboard";
import AutoSavePdfDebugBoundary from "@components/autosave/DebugBoundary";
import AutoSavePdfHost from "@components/autosave/AutoSavePdfHost";
import AutoSendPacsHost from "@components/autosave/AutoSendPacsHost";
import MppsHost from "@components/autosave/MppsHost";
import { isPacsConnectivityEnabled } from "@utils/dicom/pacsFeature";
import CareersPage from "@components/careers/CareersPage";
import ChatbaseWithDismiss from "@components/chatbase/ChatbaseWithDismiss";
import ConfirmationDialog from "@components/common/ConfirmationDialog";
import SystemOverlayWarningBanner from "@components/common/SystemOverlayWarningBanner";
import EyeTrackingRestartBanner from "@components/common/EyeTrackingRestartBanner";
import DataExportPage from "@components/data_export/DataExportPage";
import DeviceConfigurations from "@components/versions/DeviceConfigurations";
import HeadsetSceneView from "@components/HeadsetSceneView";
import Distributor from "@components/distributor_dashboard/Distributor";
import DistributorDashboard from "@components/distributor_dashboard/DistributorDashboard";
import ManagerDashboard from "@components/manager_dashboard/ManagerDashboard";
import DistributorTutorialPage from "@components/tutorial/DistributorTutorialPage";
import OfflineActivationGate from "@components/offline_activation/OfflineActivationGate";
import { isOfflineFrontendMode } from "@utils/deviceSetupNavigation";
import EventTracker from "@utils/EventTracker";
import RouteTracker from "@utils/tracking/RouteTracker";
import { FilteredWrappedTestEntryForResult } from "@components/test_entry/filter/FilteredWrappedTestEntry";
import GlobalPage from "@pages/landing/GlobalPage";
import InstallerWithWebUsb from "@components/versions/InstallerWithWebUsb";
import InvoiceSkuTypes from "@components/management/accountManagement/InvoiceSkuTypes";
import InvoicesList from "@components/management/accountManagement/InvoicesList";
import JobPage from "@components/careers/JobPage";
import LandingPage from "@pages/landing/LandingPage";
import LandingPageV2 from "@pages/landing/LandingPageV2";
import LicenseAssignmentRequests from "@components/management/accountManagement/LicenseAssignmentRequests";
import LicensesList from "@components/management/accountManagement/LicensesList";
import { ModalsProvider } from "@mantine/modals";
import NewOrder from "@components/management/accountManagement/new_order/NewOrder";
import NewPatientsList from "@components/patients_list/NewPatientsList";
import NotFoundPage from "@components/common/NotFoundPage";
import NormalDistChartSectioned from "@components/test_entry/charts/NormalDistChartSectioned";
import { Notifications } from "@mantine/notifications";
import OrderList from "@components/management/accountManagement/OrderList";
import PatientPage from "@components/patients_list/PatientPage";
import PrivacyPolicy from "@pages/landing/PrivacyPolicy";
import PrivateRoute from "./route/PrivateRoute";
import PublicRoute from "./route/PublicRoute";
import ResetPasswordPage from "@components/login_page/ResetPasswordPage";
import { Role } from "./model/role";
import RoleRoute from "./route/RoleRoute";
import { SCOPE_TYPE } from "@model";
import { SecureTokenManager } from "@utils/auth/secureTokenManager";
import TermsOfService from "@pages/landing/TermsOfService";
import TutorialPage from "@components/tutorial/TutorialPage";
import NotificationHost from "@components/notifications/NotificationHost";
import VersionControl from "@components/versions/VersionControl";
import VersionControlWebUsb from "@components/versions/VersionControlWebUsb";
import RecoveryWithWebUsb from "@components/versions/RecoveryWithWebUsb";
import VersionDetailPage from "@components/versions/VersionDetailPage";
import VersionsPage from "@components/versions/VersionsPage";
import "@utils/dayjsSetup";
import { lazyWithRetry } from "@utils/lazyWithRetry";
import { setNavigator } from "@utils/navigationService";
import { useAuthContext } from "@service";

const isNotOfflineMode = process.env.REACT_APP_MODE !== "OFFLINE";

const TestSuitePage = lazyWithRetry(() => import("./pages/TestSuitePage"));
const LoginPage = lazyWithRetry(() => import("@components/login_page/NewLoginPage"));
const OfflineActivationPage = lazyWithRetry(
  () => import("@components/offline_activation/OfflineActivationPage"),
);
const UserSettings = lazyWithRetry(() => import("@components/user_settings/UserSettings"));
const ChangePassword = lazyWithRetry(() => import("@components/change_password/ChangePassword"));
const ResultsPage = lazyWithRetry(() => import("@pages/ResultsPage"));
const TestSessionsPage = lazyWithRetry(() => import("@pages/TestSessionsPage"));
const DevicePage = lazyWithRetry(() => import("@pages/DevicePage"));
const WifiSetupPage = lazyWithRetry(() => import("@pages/WifiSetupPage"));
const OnboardingPage = lazyWithRetry(() => import("@pages/OnboardingPage"));
const SystemPage = lazyWithRetry(() => import("@pages/SystemPage"));
const HelpPage = lazyWithRetry(() => import("@pages/HelpPage"));
const DicomSettings = lazyWithRetry(() => import("@components/dicom_settings/DicomSettings"));
const TranslationManagementPage = lazyWithRetry(
  () => import("@components/translation/TranslationManagementPage"),
);
const OrganizationSettings = lazyWithRetry(
  () => import("@components/organization_settings/OrganizationSettings"),
);
const WrappedTestEntry = lazyWithRetry(() => import("@components/test_entry/WrappedTestEntry"));

const ROUTES_WITHOUT_CHAT = ["/test-result", "/filtered-test-result"];
const PARENT_PORTAL_PATH = /^\/(?:pay|consent)\/|^\/family(?:\/|$)/;
const PatientList = lazyWithRetry(
  () => import("@components/test_controller/patient_pages/PatientList"),
);
const NewTestStepper = lazyWithRetry(() => import("@components/new_test_ui/NewTestStepper"));
const PrintTestRoute = lazyWithRetry(() => import("@pages/print/PrintTestRoute"));
const NotificationSetup = lazyWithRetry(
  () => import("@components/notification_setup/NotificationSetup"),
);
const AdminConfigurations = lazyWithRetry(
  () => import("@components/admin_configurations/AdminConfigurations"),
);
const ReadingLayoutEditor = lazyWithRetry(
  () => import("@components/management/readingLayout/ReadingLayoutEditor"),
);
const PayPage = lazyWithRetry(() => import("@pages/parent-portal/PayPage"));
const PaySuccessPage = lazyWithRetry(() => import("@pages/parent-portal/PaySuccessPage"));
const FamilyLoginPage = lazyWithRetry(() => import("@pages/parent-portal/FamilyLoginPage"));
const FamilyHomePage = lazyWithRetry(() => import("@pages/parent-portal/FamilyHomePage"));
const FamilyChildPage = lazyWithRetry(() => import("@pages/parent-portal/FamilyChildPage"));
const ConsentPage = lazyWithRetry(() => import("@pages/parent-portal/ConsentPage"));
const ParentOffersList = lazyWithRetry(
  () => import("@components/management/parentPortal/ParentOffersList"),
);
const ParentOfferEditor = lazyWithRetry(
  () => import("@components/management/parentPortal/ParentOfferEditor"),
);
const ParentOfferRegistrations = lazyWithRetry(
  () => import("@components/management/parentPortal/ParentOfferRegistrations"),
);

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const { scopes, scopesLoaded } = useAuthContext();
  const [isAuthenticated, setIsAuthenticated] = useState(SecureTokenManager.isAuthenticated());
  const hasDarkModeScope = scopes.includes(SCOPE_TYPE.darkMode);

  useEffect(() => {
    setIsAuthenticated(SecureTokenManager.isAuthenticated());
  }, [location.pathname]);

  useEffect(() => {
    setNavigator(navigate);
  }, [navigate]);

  useEffect(() => {
    const beforePrint = () => {
      document.documentElement.setAttribute("data-mantine-color-scheme", "light");
    };
    const afterPrint = () => {
      const stored = localStorage.getItem("mantine-color-scheme-value");
      document.documentElement.setAttribute(
        "data-mantine-color-scheme",
        stored === "dark" ? "dark" : "light",
      );
    };
    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);
    return () => {
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, []);

  const isAuthRoute = location.pathname === "/login" || location.pathname === "/reset-password";
  const shouldShowChat =
    isNotOfflineMode &&
    isAuthenticated &&
    !isAuthRoute &&
    !ROUTES_WITHOUT_CHAT.includes(location.pathname) &&
    !PARENT_PORTAL_PATH.test(location.pathname);

  return (
    <MantineEmotionProvider>
      <MantineProvider
        theme={mainTheme as unknown}
        stylesTransform={emotionTransform}
        defaultColorScheme="light"
        forceColorScheme={
          location.pathname === "/" || (scopesLoaded && !hasDarkModeScope) ? "light" : undefined
        }
        cssVariablesResolver={cssVariablesResolver}
      >
        <ModalsProvider>
          <Notifications position="bottom-right" limit={3} />
          <HeadsetSceneView />
          <AutoSavePdfDebugBoundary>
            <AutoSavePdfHost />
            <AutoSendPacsHost />
            <MppsHost />
          </AutoSavePdfDebugBoundary>
          {shouldShowChat && <ChatbaseWithDismiss />}
          <Suspense
            fallback={
              <LoadingOverlay visible zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
            }
          >
            <EventTracker />

            <RouteTracker />

            <OfflineActivationGate />

            <ConfirmationDialog />

            {isAuthenticated && <SystemOverlayWarningBanner />}

            {isAuthenticated && <EyeTrackingRestartBanner />}

            {isAuthenticated && isNotOfflineMode && <NotificationHost />}

            <SentryRoutes>
              <Route
                path="/"
                element={
                  <PublicRoute
                    component={LandingPageV2}
                    restricted={process.env.REACT_APP_OFFLINE === "true"}
                  />
                }
              />
              <Route
                path="/oldlanding"
                element={
                  <PublicRoute
                    component={LandingPage}
                    restricted={process.env.REACT_APP_OFFLINE === "true"}
                  />
                }
              />
              <Route
                path="/global"
                element={<PublicRoute component={GlobalPage} restricted={false} />}
              />
              <Route
                path="/privacy"
                element={<PublicRoute component={PrivacyPolicy} restricted={false} />}
              />
              <Route
                path="/terms"
                element={<PublicRoute component={TermsOfService} restricted={false} />}
              />
              <Route
                path="/careers"
                element={<PublicRoute component={CareersPage} restricted={false} />}
              />
              <Route path="/job" element={<PublicRoute component={JobPage} restricted={false} />} />

              <Route
                path="/blog"
                element={<PublicRoute component={BlogList} restricted={false} />}
              />

              <Route
                path="/blog-editor"
                element={<RoleRoute component={BlogEditor} roles={[Role.Marketing, Role.Admin]} />}
              />

              <Route
                path="/blog/:slug"
                element={<PublicRoute component={BlogDetail} restricted={false} />}
              />

              <Route
                path="/reset-password"
                element={<PublicRoute component={ResetPasswordPage} restricted={false} />}
              />

              <Route
                path="/pay/:school/success"
                element={<PublicRoute component={PaySuccessPage} restricted={false} />}
              />
              <Route
                path="/pay/:school/:testKey"
                element={<PublicRoute component={PayPage} restricted={false} />}
              />
              <Route
                path="/family/login"
                element={<PublicRoute component={FamilyLoginPage} restricted={false} />}
              />
              <Route
                path="/family"
                element={<PublicRoute component={FamilyHomePage} restricted={false} />}
              />
              <Route
                path="/family/child/:childId"
                element={<PublicRoute component={FamilyChildPage} restricted={false} />}
              />
              <Route
                path="/consent/smart-screening"
                element={<PublicRoute component={ConsentPage} restricted={false} />}
              />

              <Route
                path="/activate"
                element={
                  isOfflineFrontendMode() ? (
                    <PublicRoute component={OfflineActivationPage} restricted={false} />
                  ) : (
                    <NotFoundPage />
                  )
                }
              />

              <Route
                path="/login"
                element={<PublicRoute component={LoginPage} restricted={false} />}
              />

              <Route path="/new-test-ui" element={<PrivateRoute component={NewTestStepper} />} />
              <Route path="/new-test" element={<PrivateRoute component={NewTestStepper} />} />
              <Route
                path="/lazy-eye-overview"
                element={<PrivateRoute component={LazyEyeRiskOverview} />}
              />
              <Route
                path="/neurological-assessment"
                element={<PrivateRoute component={NeurologicalAssessment} />}
              />
              <Route
                path="/vision-risk-overview"
                element={<PrivateRoute component={VisionTestResults} />}
              />
              <Route
                path="/results-summary"
                element={<PrivateRoute component={ResultsSummary} />}
              />
              <Route
                path="/cognitive-profile"
                element={<PrivateRoute component={CognitiveProfileOverview} />}
              />
              <Route
                path="/concussion-overview"
                element={<PrivateRoute component={ConcussionOverview} />}
              />
              <Route path="/test-result" element={<PrivateRoute component={WrappedTestEntry} />} />
              {/*
                Print route consumed by the backend's headless renderer.
                Not wrapped in PrivateRoute — authentication happens via the
                short-lived `rt` (render token) query param.
              */}
              <Route path="/print/test" element={<PrintTestRoute />} />
              <Route path="/gpa-baseline" element={<PrivateRoute component={BaselinePage} />} />
              <Route path="/gpa-followup" element={<PrivateRoute component={FollowUpPage} />} />
              <Route path="/gpa-summary" element={<PrivateRoute component={SummaryPage} />} />
              <Route path="/gpa-overview" element={<PrivateRoute component={OverviewPage} />} />
              <Route
                path="/change-password"
                element={<PrivateRoute component={ChangePassword} />}
              />
              <Route path="/user-settings" element={<PrivateRoute component={UserSettings} />} />
              <Route path="/progression" element={<PrivateRoute component={NewPatientsList} />} />
              <Route path="/patient-page" element={<PrivateRoute component={PatientPage} />} />
              <Route
                path="/analytics-dashboard"
                element={
                  <RoleRoute
                    component={AnalyticsDashboard}
                    roles={[Role.Distributor, Role.Admin]}
                  />
                }
              />
              <Route path="/device" element={<PrivateRoute component={DevicePage} />} />
              <Route path="/wifi-setup" element={<PrivateRoute component={WifiSetupPage} />} />
              <Route path="/onboarding" element={<PrivateRoute component={OnboardingPage} />} />
              <Route path="/system" element={<PrivateRoute component={SystemPage} />} />
              <Route
                path="/dicom-settings"
                element={
                  isPacsConnectivityEnabled() ? (
                    <PrivateRoute component={DicomSettings} allowedScopes={[SCOPE_TYPE.dicom]} />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
              <Route path="/organization-dashboard" element={<Navigate to="/device" replace />} />
              <Route
                path="/organization-settings"
                element={<PrivateRoute component={OrganizationSettings} />}
              />
              <Route path="/patients" element={<PrivateRoute component={PatientList} />} />
              <Route
                path="/data-export"
                element={
                  <PrivateRoute
                    component={DataExportPage}
                    allowedScopes={[SCOPE_TYPE.dataExport]}
                  />
                }
              />
              <Route
                path="/translations"
                element={
                  <PrivateRoute
                    component={TranslationManagementPage}
                    allowedScopes={[SCOPE_TYPE.translation]}
                  />
                }
              />
              <Route
                path="/filtered-test-result"
                element={<PrivateRoute component={FilteredWrappedTestEntryForResult} />}
              />
              <Route path="/list" element={<PrivateRoute component={ResultsPage} />} />
              <Route
                path="/test-sessions"
                element={
                  <PrivateRoute
                    component={TestSessionsPage}
                    allowedScopes={[SCOPE_TYPE.testSession]}
                  />
                }
              />
              <Route path="/versions" element={<PrivateRoute component={VersionsPage} />} />
              <Route
                path="/versions/:version"
                element={<PrivateRoute component={VersionDetailPage} />}
              />
              <Route path="/tutorial" element={<PrivateRoute component={TutorialPage} />} />
              <Route path="/help" element={<PrivateRoute component={HelpPage} />} />
              <Route
                path="/tutorial/distributor"
                element={
                  <RoleRoute
                    component={DistributorTutorialPage}
                    roles={[Role.Distributor, Role.Admin]}
                  />
                }
              />
              <Route
                path="/tutorial/admin"
                element={<RoleRoute component={AdminTutorialPage} roles={[Role.Admin]} />}
              />
              <Route
                path="/version-control"
                element={<PrivateRoute component={VersionControl} />}
              />
              <Route
                path="/usb-version-control"
                element={<PrivateRoute component={VersionControlWebUsb} />}
              />
              <Route
                path="/device-setup"
                element={<PrivateRoute component={RecoveryWithWebUsb} />}
              />
              <Route
                path="/admin-dashboard"
                element={<RoleRoute component={AdminDashboard} roles={[Role.Admin]} />}
              />
              <Route
                path="/admin-configurations"
                element={<RoleRoute component={AdminConfigurations} roles={[Role.Admin]} />}
              />
              <Route path="/audit-trail" element={<PrivateRoute component={AuditTrail} />} />
              <Route
                path="/scope-management"
                element={<RoleRoute component={ScopeManagement} roles={[Role.Admin]} />}
              />
              <Route
                path="/ra-text-editor"
                element={
                  <PrivateRoute
                    component={ReadingLayoutEditor}
                    allowedScopes={[SCOPE_TYPE.raTextEditor]}
                  />
                }
              />
              <Route
                path="/dconfigs"
                element={<RoleRoute component={DefaultConfigs} roles={[Role.Admin]} />}
              />
              <Route
                path="/product-management"
                element={<RoleRoute component={ProductManagement} roles={[Role.Admin]} />}
              />
              <Route
                path="/product-status"
                element={<RoleRoute component={ProductStatusPage} roles={[Role.Admin]} />}
              />
              <Route
                path="/system-logs"
                element={<RoleRoute component={SystemLogs} roles={[Role.Admin]} />}
              />
              <Route
                path="/notification-setup"
                element={<RoleRoute component={NotificationSetup} roles={[Role.Admin]} />}
              />
              <Route
                path={VERSION_MANAGEMENT_PATH}
                element={<RoleRoute component={VersionManagementPage} roles={[Role.Admin]} />}
              />
              <Route
                path={NEW_RELEASE_PATH}
                element={<RoleRoute component={NewReleasePage} roles={[Role.Admin]} />}
              />
              <Route
                path={releasePagePath(":version")}
                element={<RoleRoute component={ReleasePage} roles={[Role.Admin]} />}
              />
              <Route
                path="/installer"
                element={
                  <PrivateRoute
                    component={InstallerWithWebUsb}
                    allowedScopes={[SCOPE_TYPE.installer]}
                  />
                }
              />
              <Route
                path="/device-config"
                element={<RoleRoute component={DeviceConfigurations} roles={[Role.Admin]} />}
              />
              <Route
                path="/dist"
                element={<RoleRoute component={DistributorManagement} roles={[Role.Admin]} />}
              />
              <Route
                path="/test-suite"
                element={
                  <PrivateRoute
                    component={TestSuitePage}
                    allowedScopes={[SCOPE_TYPE.testSession]}
                  />
                }
              />
              <Route
                path="/distributor-dashboard"
                element={
                  <RoleRoute
                    component={DistributorDashboard}
                    roles={[Role.Distributor, Role.Admin]}
                  />
                }
              />
              <Route
                path="/manager-dashboard"
                element={<RoleRoute component={ManagerDashboard} roles={[Role.Manager]} />}
              />
              <Route
                path="/distributor"
                element={
                  <RoleRoute component={Distributor} roles={[Role.Distributor, Role.Admin]} />
                }
              />

              <Route
                path="/orders"
                element={<RoleRoute component={OrderList} roles={[Role.Distributor, Role.Admin]} />}
              />

              <Route
                path="/create-order"
                element={<RoleRoute component={NewOrder} roles={[Role.Distributor, Role.Admin]} />}
              />

              <Route
                path="/licenses"
                element={
                  <RoleRoute component={LicensesList} roles={[Role.Distributor, Role.Admin]} />
                }
              />

              <Route
                path="/invoices"
                element={
                  <RoleRoute component={InvoicesList} roles={[Role.Distributor, Role.Admin]} />
                }
              />

              <Route
                path="/invoice-sku-types"
                element={<RoleRoute component={InvoiceSkuTypes} roles={[Role.Admin]} />}
              />

              <Route
                path="/screening-payments"
                element={<RoleRoute component={ParentOffersList} roles={[Role.Admin]} />}
              />
              <Route
                path="/screening-payments/new"
                element={<RoleRoute component={ParentOfferEditor} roles={[Role.Admin]} />}
              />
              <Route
                path="/screening-payments/registrations"
                element={<RoleRoute component={ParentOfferRegistrations} roles={[Role.Admin]} />}
              />
              <Route
                path="/screening-payments/:slug"
                element={<RoleRoute component={ParentOfferEditor} roles={[Role.Admin]} />}
              />

              <Route
                path="/assign-requests"
                element={
                  <RoleRoute
                    component={LicenseAssignmentRequests}
                    roles={[Role.Distributor, Role.Admin]}
                  />
                }
              />
              <Route
                path="/chart"
                element={<PrivateRoute component={NormalDistChartSectioned} />}
              />

              {/* Legacy blog routes for redirect purposes. Do not remove */}
              <Route
                path="/the-role-of-visual-field-analysis-in-managing-diabetes-related-vision-impairment"
                element={
                  <Navigate
                    to="/blog/the-role-of-visual-field-analysis-in-managing-diabetes-related-vision-impairment"
                    replace
                  />
                }
              />
              <Route
                path="/revolutionizing-eye-health-access-in-eswatini-with-oculera"
                element={
                  <Navigate
                    to="/blog/revolutionizing-eye-health-access-in-eswatini-with-oculera"
                    replace
                  />
                }
              />
              <Route
                path="/the-end-of-dread-why-patients-and-clinics-prefer-portable-visual-field-testing"
                element={
                  <Navigate
                    to="/blog/the-end-of-dread-why-patients-and-clinics-prefer-portable-visual-field-testing"
                    replace
                  />
                }
              />

              <Route path="*" element={<NotFoundPage />} />
            </SentryRoutes>
          </Suspense>
        </ModalsProvider>
      </MantineProvider>
    </MantineEmotionProvider>
  );
}

// Enable Concurrent Mode
// https://reactjs.org/docs/concurrent-mode-adoption.html#enabling-concurrent-mode
// createRoot(document.getElementById('root')).render(<App />);
